import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react'

const REGISTER_MODAL = 'core/registerModal'
const SHOW_MODAL = 'core/showModal'
const CLOSE_MODAL = 'core/closeModal'
const PERSIST_PURGE = 'persist/PURGE'

interface ModalInfo {
  visible: boolean
}

export interface ModalState {
  [id: string]: ModalInfo
}

export interface ModalAction {
  type: string
  payload: string
}

export const modalInitialState: ModalState = {}

export const modalReducer = (state: ModalState = modalInitialState, action: ModalAction): ModalState => {
  const modalKey = action.payload
  switch (action.type) {
    case REGISTER_MODAL:
      if (Object.keys(state).includes(modalKey)) {
        return state
      }

      return { ...state, [modalKey]: { visible: false } }

    case SHOW_MODAL:
      if (Object.keys(state).includes(modalKey)) {
        return { ...state, [modalKey]: { visible: true } }
      }

      return state

    case CLOSE_MODAL:
      if (Object.keys(state).includes(modalKey)) {
        return { ...state, [modalKey]: { visible: false } }
      }
      return state

    case PERSIST_PURGE:
      return modalInitialState

    default:
      return state
  }
}

export const registerModal = (modalId: string): ModalAction => ({ type: REGISTER_MODAL, payload: modalId })
export const showModal = (modalId: string): ModalAction => ({ type: SHOW_MODAL, payload: modalId })
export const closeModal = (modalId: string): ModalAction => ({ type: CLOSE_MODAL, payload: modalId })

interface ModalContextProps {
  modals: {
    [id: string]: ModalInfo
  }
  dispatch: React.Dispatch<ModalAction>
}

const defaultContext: ModalContextProps = {
  modals: {},
  dispatch: () => {},
}

interface ModalProvideConfig {
  children: React.ReactNode
  modals?: {
    [id: string]: ModalInfo
  }
  dispatch?: React.Dispatch<ModalAction>
}

export const ModalContext = createContext(defaultContext)

export const ModalProvider: React.FunctionComponent<ModalProvideConfig> = ({ children, modals, dispatch }) => {
  const [_modals, _dispatch] = useReducer(modalReducer, {})
  const isUncontrolled = modals == null || dispatch == null

  const context = {
    modals: isUncontrolled ? _modals : modals,
    dispatch: isUncontrolled ? _dispatch : dispatch,
  }

  return <ModalContext.Provider value={context}>{children}</ModalContext.Provider>
}

interface ModalHookReturnType {
  visible: boolean
  showModal: () => void
  closeModal: () => void
  modals: {
    [id: string]: ModalInfo
  }
}

export const useModal = (id: string): ModalHookReturnType => {
  const { modals, dispatch } = useContext(ModalContext)

  const show = useCallback(() => dispatch(showModal(id)), [id])
  const close = useCallback(() => dispatch(closeModal(id)), [id])

  return useMemo(
    () => ({
      visible: modals[id] ? modals[id].visible : false,
      showModal: show,
      closeModal: close,
      modals,
    }),
    [modals[id], modals]
  )
}
